import { useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../../../helpers'
import { CompanyStatus } from '../../../models'
import { CompanyWithInvoiceCode } from '../Property/types'

export const ADMIN_VENDORS_LIST_KEY = 'admin-vendors-list'

export interface PaginatedVendorsResponse {
    count: number
    next: string | null
    previous: string | null
    results: CompanyWithInvoiceCode[]
}

export interface AdminVendorsFilterParams {
    search?: string
    state?: string | null
    organizationId?: number | null
    inHouse?: boolean | null
    status?: CompanyStatus | 'ALL'
    premiumOnly?: boolean
    ordering?: string
}

export interface AdminVendorsListParams extends AdminVendorsFilterParams {
    page: number
    pageSize: number
}

export const buildAdminVendorsFilterParams = (
    params: AdminVendorsFilterParams,
): URLSearchParams => {
    const queryParams = new URLSearchParams()

    if (params.search?.trim()) {
        queryParams.append('search', params.search.trim())
    }
    if (params.state?.trim()) {
        queryParams.append('state', params.state.trim())
    }
    if (params.organizationId != null) {
        queryParams.append('organization_id', params.organizationId.toString())
    }
    if (params.inHouse === true) {
        queryParams.append('in_house', 'true')
    } else if (params.inHouse === false) {
        queryParams.append('in_house', 'false')
    }
    if (params.status && params.status !== 'ALL') {
        queryParams.append('status', params.status)
    }
    if (params.premiumOnly) {
        queryParams.append('premium_only', 'true')
    }
    if (params.ordering) {
        queryParams.append('ordering', params.ordering)
    }

    return queryParams
}

const buildListParams = (params: AdminVendorsListParams): URLSearchParams => {
    const queryParams = buildAdminVendorsFilterParams(params)
    queryParams.append('page', params.page.toString())
    queryParams.append('page_size', params.pageSize.toString())
    return queryParams
}

export const useAdminVendorsList = (params: AdminVendorsListParams) => {
    const { data: response, isLoading, isFetching, isError, error } = useQuery<
        AxiosResponse<PaginatedVendorsResponse>,
        Error
    >({
        queryKey: [ADMIN_VENDORS_LIST_KEY, params],
        queryFn: () =>
            axiosInstance.get(
                `/account-manager/vendors/admin-list/?${buildListParams(
                    params,
                ).toString()}`,
            ),
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const data = response?.data

    return {
        vendors: data?.results ?? [],
        count: data?.count ?? 0,
        isLoading,
        isFetching,
        isError,
        error,
    }
}
